import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LogOut, Settings } from 'lucide-react';

import { useLogout, useSession } from '@/features/auth';

import styles from './UserMenu.module.css';

/**
 * Menu osoby zalogowanej — **jedyna rzecz po prawej stronie paska**.
 *
 * Wcześniej stały tam dwa przyciski: przełącznik motywu i „Wyloguj". Przełącznik był
 * **dubletem** — sekcja „Konto i wygląd" ma motyw i język od dnia, w którym powstała
 * (decyzja 78) — więc jego usunięcie nic nie zabiera. „Wyloguj" schodzi tutaj, razem
 * z wejściem w ustawienia. ⚠ Przełącznik **zostaje na ekranie logowania**: tam nie ma
 * ustawień, bo nie ma jeszcze konta.
 *
 * ── Dlaczego napisane ręcznie, a nie z biblioteki ─────────────────────────────
 * Ten sam powód, co przy `OmniboxDialog`: menu ma **dwie pozycje i zero stanu**.
 * Gotowy `DropdownMenu` (Radix) wniósłby zależność, żeby rozwiązać problem, którego
 * tu nie ma — pułapkę ogniska w drzewie o wielu poziomach. Wzorzec WAI-ARIA dla menu
 * przycisku to obsługa Escape, kliknięcia poza, strzałek i zwrotu ogniska; to jest
 * poniżej, w całości i jawnie. ⭐ Gdyby menu urosło do podmenu albo pozycji
 * przełączanych, decyzja się odwraca — wtedy biblioteka zaczyna zarabiać.
 */

/** Inicjały jako zastępnik avatara. Docelowo w kółku siedzi obrazek. */
function initialsOf(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function UserMenu() {
  const { t } = useTranslation();
  const { data: user } = useSession();
  const logout = useLogout();

  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);

  // Zamknięcie kliknięciem poza i Escape. ⚠ `pointerdown`, nie `click`: przy `click`
  // menu zamykałoby się PO tym, jak kliknięcie doszło do elementu pod spodem, więc
  // jedno kliknięcie robiłoby dwie rzeczy naraz.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        // Ognisko wraca na przycisk — inaczej po zamknięciu klawiaturą człowiek
        // zostaje w powietrzu i musi tabulatorem szukać, gdzie był.
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Otwarcie stawia ognisko na pierwszej pozycji — wzorzec menu przycisku z WAI-ARIA.
  useEffect(() => {
    if (isOpen) {
      firstItemRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div className={styles['wrap']} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles['avatar']}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        // Nazwa dostępna niesie imię, bo same inicjały niczego nie mówią czytnikowi ekranu.
        aria-label={t('app.user.menu', { name: user?.displayName ?? '' })}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        {user ? initialsOf(user.displayName) : '—'}
      </button>

      {isOpen ? (
        <div className={styles['menu']} role="menu">
          {/* Nagłówek nie jest pozycją menu — to odpowiedź na „kim teraz jestem".
              ⭐ Role są tu istotne, nie ozdobne: przy płaskich zbiorach (decyzja 81)
              to jedyne miejsce, w którym człowiek zobaczy, czym jest — a od tego zależy,
              których pozycji menu w ogóle nie widzi. */}
          <div className={styles['who']}>
            <b>{user?.displayName}</b>
            <small>{user?.login}</small>

            {user && user.roles.length > 0 ? (
              <span className={styles['roles']}>
                {user.roles.map((role) => (
                  <span key={role} className={styles['role']}>
                    {t(`settings.account.role.${role}`, { defaultValue: role })}
                  </span>
                ))}
              </span>
            ) : null}
          </div>

          <Link
            ref={firstItemRef}
            role="menuitem"
            className={styles['item']}
            to="/settings"
            onClick={() => setIsOpen(false)}
          >
            <Settings size={17} aria-hidden="true" />
            {t('app.nav.settings')}
          </Link>

          <button
            type="button"
            role="menuitem"
            className={`${styles['item']} ${styles['danger']}`}
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
          >
            <LogOut size={17} aria-hidden="true" />
            {t('auth.logout.action')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
